// DESCRIPTION: Step 2 — business identity. Business name, timezone, currency,
// and optional logo. All fields are optional; the step is skippable.

import { __ } from '@wordpress/i18n';
import WizardShell from '../components/WizardShell';
import { CURRENCIES } from '../../utils/currencies';

const TIMEZONES = [
	'UTC',
	'America/Los_Angeles',
	'America/Denver',
	'America/Chicago',
	'America/New_York',
	'America/Sao_Paulo',
	'Europe/London',
	'Europe/Paris',
	'Europe/Berlin',
	'Europe/Istanbul',
	'Africa/Cairo',
	'Asia/Dubai',
	'Asia/Karachi',
	'Asia/Dhaka',
	'Asia/Bangkok',
	'Asia/Singapore',
	'Asia/Hong_Kong',
	'Asia/Tokyo',
	'Australia/Sydney',
	'Pacific/Auckland',
];

/**
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.value      { business_name, timezone, currency, logo_id }
 * @param {Function} props.onChange   (next) => void
 * @param {Function} props.onBack
 * @param {Function} props.onSkip
 * @param {Function} props.onContinue
 * @param {number}   props.progress
 * @param {boolean}  props.isLoading
 *
 * @return {JSX.Element}
 */
export default function IdentityStep( {
	value,
	onChange,
	onBack,
	onSkip,
	onContinue,
	progress,
	isLoading,
} ) {
	const update = ( key, next ) => onChange( { ...value, [ key ]: next } );

	return (
		<WizardShell
			stepKey="identity"
			progress={ progress }
			onBack={ onBack }
			onSkip={ onSkip }
			onContinue={ onContinue }
			isLoading={ isLoading }
		>
			<div className="nemtly-wizard-step">
				<h2 className="nemtly-wizard-headline">
					{ __( 'Business Identity', 'nemtly-booking' ) }
				</h2>
				<p className="nemtly-wizard-lede">
					{ __(
						'Tell us a bit about your brand.',
						'nemtly-booking'
					) }
				</p>

				<div className="nemtly-wizard-field">
					<label htmlFor="nemtly-wizard-business-name">
						{ __( 'Business Name', 'nemtly-booking' ) }
					</label>
					<input
						id="nemtly-wizard-business-name"
						type="text"
						value={ value.business_name }
						onChange={ ( e ) =>
							update( 'business_name', e.target.value )
						}
						placeholder={ __(
							'e.g. Acme Studio',
							'nemtly-booking'
						) }
					/>
				</div>

				<div className="nemtly-wizard-field-row">
					<div className="nemtly-wizard-field">
						<label htmlFor="nemtly-wizard-timezone">
							{ __( 'Timezone', 'nemtly-booking' ) }
						</label>
						<select
							id="nemtly-wizard-timezone"
							value={ value.timezone }
							onChange={ ( e ) =>
								update( 'timezone', e.target.value )
							}
						>
							{ TIMEZONES.map( ( tz ) => (
								<option key={ tz } value={ tz }>
									{ tz }
								</option>
							) ) }
						</select>
					</div>

					<div className="nemtly-wizard-field">
						<label htmlFor="nemtly-wizard-currency">
							{ __( 'Currency', 'nemtly-booking' ) }
						</label>
						<select
							id="nemtly-wizard-currency"
							value={ value.currency }
							onChange={ ( e ) =>
								update( 'currency', e.target.value )
							}
						>
							{ CURRENCIES.map( ( c ) => (
								<option
									key={ c.code }
									value={ c.code }
								>{ `${ c.code } — ${ c.name }` }</option>
							) ) }
						</select>
					</div>
				</div>
			</div>
		</WizardShell>
	);
}
